import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import {
  LuArrowRight,
  LuCalendar,
  LuBuilding,
  LuBuilding2,
  LuChartLine,
  LuCpu,
  LuHeartPulse,
  LuLandmark,
  LuZap,
} from 'react-icons/lu';

import GradientCTA from '../app/components/GradientCTA';
import HeroCtaActions from '../app/components/HeroCtaActions';
import HeroAnimated from '../app/components/HeroAnimated';
import ImpactResultsSection from '../app/components/ImpactResultsSection';
import IndustriesWorldMap from '../app/components/IndustriesWorldMap';
import RevealSection from '../app/components/RevealSection';
import SbrIcon from '../app/components/SbrIcon';
import TrustedLogoMarquee from '../app/components/TrustedLogoMarquee';
import { SBR_IMPACT_STATS } from '../data/sbrImpactStats';

type IndustryTone = 'teal' | 'blue' | 'purple';

const industryCards: {
  title: string;
  desc: string;
  Icon: IconType;
  tone: IndustryTone;
}[] = [
  {
    title: 'Financial Services',
    desc: 'From investment firms to fintech leaders, we deliver talent that strengthens performance and manages risk.',
    Icon: LuLandmark,
    tone: 'teal',
  },
  {
    title: 'Technology',
    desc: "Engineers, product leaders, and technical experts who build, scale, and secure what's next.",
    Icon: LuCpu,
    tone: 'blue',
  },
  {
    title: 'Manufacturing & Industrial',
    desc: 'Operations, engineering, and supply chain talent that drives efficiency and innovation.',
    Icon: LuBuilding2,
    tone: 'purple',
  },
  {
    title: 'Healthcare & Life Sciences',
    desc: 'Clinical, operational, and commercial talent improving lives and outcomes.',
    Icon: LuHeartPulse,
    tone: 'teal',
  },
  {
    title: 'Energy & Utilities',
    desc: 'Powering the future with specialized talent across infrastructure and operations.',
    Icon: LuZap,
    tone: 'teal',
  },
  {
    title: 'Professional Services',
    desc: 'Advisors, analysts, and client service experts who deliver results and build trust.',
    Icon: LuChartLine,
    tone: 'blue',
  },
  {
    title: 'Public Sector',
    desc: 'Experienced professionals committed to mission-driven impact and communities.',
    Icon: LuBuilding,
    tone: 'purple',
  },
];

export default function IndustriesPage() {
  return (
    <>
      <section className="ind-hero text-white">
        <div className="ind-hero__main">
          <Container className="ind-hero__container">
            <Row className="ind-hero__row align-items-center gy-5">
              <Col lg={5} className="ind-hero__copy-col">
                <HeroAnimated className="ind-hero__animated">
                  <div className="ind-hero__headline">
                    <p data-hero-item className="ind-hero__eyebrow mb-2">
                      Industries We Serve
                    </p>
                    <h1 data-hero-item className="display-5 fw-bold mb-0">
                      <span className="ind-hero__title-line">Deep expertise across industries.</span>
                      <span className="ind-hero__title-line text-gradient-teal-blue">
                        Proven results everywhere.
                      </span>
                    </h1>
                  </div>

                  <div className="ind-hero__visual ind-hero__visual--mobile d-lg-none">
                    <IndustriesWorldMap />
                  </div>

                  <div className="ind-hero__body">
                    <p data-hero-item className="lead text-white-50 mb-0 pe-lg-2">
                      We partner with companies across industries to deliver high-impact talent that drives growth,
                      innovation, and competitive advantage.
                    </p>
                  </div>

                  <div data-hero-item className="ind-hero__cta-row">
                    <HeroCtaActions
                      block="ind-hero"
                      secondary={{
                        to: '/roles-we-fill',
                        label: 'Explore Roles We Fill',
                        shortLabel: 'Explore Roles',
                        icon: LuArrowRight,
                        iconPosition: 'end',
                      }}
                    />
                  </div>
                </HeroAnimated>
              </Col>

              <Col lg={7} className="ind-hero__visual-col d-none d-lg-block">
                <RevealSection>
                  <IndustriesWorldMap />
                </RevealSection>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      <section className="bg-white py-5 py-lg-6">
        <RevealSection>
          <Container>
            <div className="ind-industries-header text-center">
              <h2 className="sbr-section__title fw-bold mb-2">Industries We Serve</h2>
              <p className="ind-industries-header__subtitle mb-0">
                Specialized talent. Industry knowledge. Measurable impact.
              </p>
            </div>
            <div className="ind-industries-grid">
              {industryCards.map((ind) => (
                <article key={ind.title} className={`ind-industry-card ind-industry-card--${ind.tone}`}>
                  <div className="ind-industry-card__icon" aria-hidden>
                    <SbrIcon icon={ind.Icon} size={24} />
                  </div>
                  <h3 className="ind-industry-card__title">{ind.title}</h3>
                  <p className="ind-industry-card__desc">{ind.desc}</p>
                  <Link className="ind-industry-card__link" to="/book-a-call">
                    View Roles
                    <SbrIcon icon={LuArrowRight} size={16} className="ms-1" aria-hidden />
                  </Link>
                </article>
              ))}
            </div>
          </Container>
        </RevealSection>
      </section>

      <section className="ind-trusted bg-white">
        <Container className="ind-trusted__inner">
          <Row className="align-items-center gy-3">
            <Col lg={3} xl={3} className="ind-trusted__label-col">
              <p className="ind-trusted__label text-sbr-blue mb-0">Trusted by companies across industries</p>
            </Col>
            <Col lg={9} xl={9}>
              <TrustedLogoMarquee theme="light" />
            </Col>
          </Row>
        </Container>
      </section>

      <ImpactResultsSection
        reveal
        title={
          <Fragment>
            Industry knowledge.{' '}
            <span className="text-gradient-teal-blue">Better hiring outcomes.</span>
          </Fragment>
        }
        lead="Our team brings hands-on industry experience and deep network reach to every search."
        stats={SBR_IMPACT_STATS}
      />

      <GradientCTA
        icon={<SbrIcon icon={LuCalendar} size={26} />}
        title={
          <Fragment>
            <div>Let&apos;s solve your toughest hiring challenges.</div>
            <div className="sbr-cta__subtitle fw-normal opacity-90 mt-2 mb-0">
              Connect with our team to get started.
            </div>
          </Fragment>
        }
      />
    </>
  );
}
